import React, { useEffect, useMemo, useState } from 'react'
import { useLazyQuery, useReactiveVar } from '@apollo/client'
import moment from 'moment'

import { currentUserDetails } from '../api/apollo/cache/variables'
import { getCompanyAccountsAndUsers } from '../api/apollo/graphql/company-client'
import { getAccountUsageData } from '../api/apollo/graphql/report-client'
import { SelectedDimensions } from '../api/types'
import ButtonTabs from '../components/button-tabs'
import Intro from '../components/intro'
import Layout from '../components/layout'
import SiteWrapper from '../components/site-wrapper'
import UsageReportFilters from '../components/usage-report-filter'
import UsageReportTab, {
  UsageSuccessMetrics,
  successMetrics,
} from '../components/usage-report-tab'
import useResize from '../hooks/useResize'
import styles from '../styles/usage-report-page.module.scss'
import { DimensionFilter } from '../__gql-types__/graphql'

interface MapIDs {
  [id: string]: string
}

const UsageReportPage = () => {
  const { companyID } = useReactiveVar(currentUserDetails)

  // TODO: Write a custom merge function for this, when the success metric changes
  const [
    getUsageData,
    {
      data: usageData,
      loading: loadingUsageData,
      error: errorFetchingUsageData,
    },
  ] = useLazyQuery(getAccountUsageData, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  })

  const [
    getAccountsAndUsers,
    { data: accountListData, loading: loadingAccountList },
  ] = useLazyQuery(getCompanyAccountsAndUsers)

  useEffect(() => {
    if (companyID) getAccountsAndUsers()
  }, [companyID])

  const accountList = useMemo(() => {
    if (!accountListData) return []

    return accountListData.currentCompany.accountList
  }, [accountListData])

  const accountUsageData = useMemo(() => {
    if (!usageData) return null

    return usageData.report
  }, [usageData])

  const yesterday = new Date(Date.now())
  yesterday.setDate(yesterday.getDate() - 1)

  const screenWidth = useResize()
  const isMobile = screenWidth <= 768

  const [width, setWidth] = useState(0)
  const [currentDate, setCurrentDate] = useState('thisYear')
  const [startDate, setStartDate] = useState(
    moment(yesterday).subtract(365, 'days').toDate(),
  )
  const [endDate, setEndDate] = useState(yesterday)
  const [granularity, setGranularity] = useState<Granularity>('monthly')
  const [graphDateRange, setGraphDateRange] = useState<string[] | null>(null)
  const [graphData, setGraphData] = useState<DashboardGraphData[] | null>(null)
  const [graphTableTotals, setGraphTableTotals] = useState<number[] | null>(
    null,
  )
  const [currentIndex, setCurrentIndex] = useState(0)
  const [successMetric, setSuccessMetric] = useState<UsageSuccessMetrics>(
    'badCodesBlocked',
  )
  const [stackDimension, setStackDimension] = useState<'creation type' | ''>('')
  const [selectedDimensions, setSelectedDimensions] = useState<
    SelectedDimensions
  >({})
  const [userIDs, setUserIDs] = useState<MapIDs>({})
  const [accountIDs, setAccountIDs] = useState<MapIDs>({})

  // Fetch initial data: badCodesBlocked
  useEffect(() => {
    getUsageData({
      variables: {
        activeUsers: false,
        badCodesBlocked: true,
        codesCreated: false,
        invalidCodesBreakdown: false,
        logins: false,
        totalUsers: false,
        startDate: 'thisYear',
        granularity: 'monthly',
      },
    })
  }, [])

  // Set graph info
  useEffect(() => {
    if (
      accountUsageData &&
      accountUsageData[successMetric] &&
      accountUsageData[successMetric]?.graphData
    ) {
      setGraphDateRange(accountUsageData[successMetric]?.dateRange || null)
      setGraphTableTotals(
        accountUsageData[successMetric]?.dateTotalRows || null,
      )
      // Active users should show a split with total users
      if (
        successMetric === 'activeUsers' &&
        accountUsageData.activeUsers?.graphData &&
        accountUsageData.totalUsers?.graphData
      ) {
        const combinedGraphData = [...accountUsageData.activeUsers.graphData]

        const {
          dimensionValues: activeUsersValues,
        } = accountUsageData.activeUsers.graphData[0]

        const {
          dimensionValues: totalUsersValues,
        } = accountUsageData.totalUsers.graphData[0]

        const totalUsersData = {
          dimensionName: 'Dormant users',
          dimensionValues: totalUsersValues.map((val, index) =>
            Math.max(0, val - activeUsersValues[index]),
          ),
        }

        combinedGraphData.push(totalUsersData)

        setGraphData(combinedGraphData)
      } else {
        setGraphData(accountUsageData[successMetric]?.graphData || null)
      }
    }
  }, [accountUsageData, successMetric])

  // Get filterable items
  const availableDimensions = useMemo(() => {
    if (accountList.length === 0) return []

    const accountsObj: MapIDs = {}
    const uniqueUsersObj: MapIDs = {}

    accountList.forEach((account) => {
      if (account.accountID) {
        accountsObj[account.accountName] = account.accountID
      }

      account.userAccountProfiles.forEach((profile) => {
        if (
          profile.userID &&
          !Object.prototype.hasOwnProperty.call(uniqueUsersObj, profile.userID)
        ) {
          uniqueUsersObj[profile.userEmail] = profile.userID
        }
      })
    })

    setAccountIDs(accountsObj)
    setUserIDs(uniqueUsersObj)

    return [
      {
        dimensionParameterID: 'account',
        dimensionName: 'workspace',
        parameterType: 'string',
        dimensionOptions: Object.keys(accountsObj),
      },
      {
        dimensionParameterID: 'user',
        dimensionName: 'user',
        parameterType: 'string',
        dimensionOptions: Object.keys(uniqueUsersObj),
      },
    ]
  }, [accountList])

  // Refetch data whenever a filter option is changed
  useEffect(() => {
    let _startDate = currentDate
    let _endDate: string | undefined

    if (currentDate === 'custom') {
      _startDate = moment(startDate).format('YYYY-MM-DD')
      _endDate = moment(endDate).format('YYYY-MM-DD')
    }

    const dimensionFilters: DimensionFilter[] = []

    // Create correctly shaped dimension filter object for query
    Object.keys(selectedDimensions).forEach((key) => {
      if (selectedDimensions[key].dimensionOptions.length > 0) {
        const { dimensionParameterID, dimensionOptions } = selectedDimensions[
          key
        ]

        let _dimensionOptions = [...dimensionOptions]

        if (dimensionParameterID === 'user') {
          _dimensionOptions = dimensionOptions.map((option) => userIDs[option])
        }

        if (dimensionParameterID === 'account') {
          _dimensionOptions = dimensionOptions.map(
            (option) => accountIDs[option],
          )
        }

        dimensionFilters.push({
          dimensionParameterID,
          dimensionName: key,
          dimensionOptions: _dimensionOptions,
        })
      }
    })

    getUsageData({
      variables: {
        activeUsers: successMetric === 'activeUsers',
        badCodesBlocked: successMetric === 'badCodesBlocked',
        codesCreated: successMetric === 'codesCreated',
        invalidCodesBreakdown: successMetric === 'invalidCodesBreakdown',
        logins: successMetric === 'logins',
        totalUsers: successMetric === 'activeUsers',
        startDate: _startDate,
        endDate: _endDate,
        granularity,
        filterList:
          dimensionFilters.length > 0 ? { dimensionFilters } : undefined,
      },
    })
  }, [
    currentDate,
    startDate,
    endDate,
    granularity,
    successMetric,
    selectedDimensions,
  ])

  return (
    <SiteWrapper>
      <Layout width={1600}>
        <div
          ref={(element) => {
            if (element) {
              const container = element.getBoundingClientRect()
              setWidth(container.width - 42)
            }
          }}
        >
          {errorFetchingUsageData ? (
            <Intro title="Value and usage">
              <p>We are having trouble loading your dashboard data.</p>
            </Intro>
          ) : (
            <>
              <Intro title="Value and usage" className={styles.intro}>
                <p>
                  Create reports which show who is using Uplifter and how it is
                  adding value.
                </p>
              </Intro>

              <UsageReportFilters
                isMobile={isMobile}
                currentDate={currentDate}
                setCurrentDate={setCurrentDate}
                granularity={granularity}
                setGranularity={setGranularity}
                setStartDate={setStartDate}
                setEndDate={setEndDate}
                availableDimensions={availableDimensions}
                selectedDimensions={selectedDimensions}
                setSelectedDimensions={setSelectedDimensions}
                loading={accountList.length === 0 || loadingAccountList}
              />

              <ButtonTabs
                className={styles.buttonTabs}
                selected={currentIndex}
                isTopOfBox
                tabsLabels={[
                  'Invalid links prevented',
                  'Link errors breakdown',
                  'Links created',
                  'Logins',
                  'Active users',
                ]}
                type="tabs"
                onChange={(index: number) => {
                  setCurrentIndex(index)
                  setSuccessMetric(successMetrics[index])
                  setStackDimension(index === 1 ? 'creation type' : '')
                }}
              >
                {successMetrics.map((currMetric) => {
                  return (
                    <UsageReportTab
                      loading={loadingUsageData}
                      accountUsageData={accountUsageData}
                      key={currMetric}
                      successMetric={currMetric}
                      width={width}
                      screenWidth={screenWidth}
                      isMobile={isMobile}
                      stackDimension={stackDimension}
                      graphDateRange={graphDateRange}
                      graphData={graphData}
                      graphTableTotals={graphTableTotals}
                      granularity={granularity}
                      startDate={startDate}
                      endDate={endDate}
                      currentDate={currentDate}
                    />
                  )
                })}
              </ButtonTabs>
            </>
          )}
        </div>
      </Layout>
    </SiteWrapper>
  )
}

export default UsageReportPage
